import * as React from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha, keyframes, styled } from '@mui/material/styles';

const ripple = keyframes`
    0%   { transform: translate(-50%, -50%) scale(0.15); opacity: 0.45; filter: blur(1px); }
    70%  { opacity: 0.18; }
    100% { transform: translate(-50%, -50%) scale(2.20); opacity: 0; filter: blur(8px); }
`;
const WavesRoot = styled(Box)(() => ({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
}));

const Wave = styled(Box, {
  shouldForwardProp: (prop) =>
    !['$size', '$color', '$duration', '$delay', '$width', '$height'].includes(prop as string),
})<{
  $size: number;
  $height: number;
  $width: number;
  $color: string;
  $duration: number;
  $delay: number;
}>(({ $size, $color, $duration, $delay, $width, $height }) => ({
  position: 'absolute',
  left: '50%',
  top: '50%',
  width: $width * 5 || $size,
  height: $height * 5 || $size,
  borderRadius: '50%',
  border: `4px solid ${alpha($color, 0.55)}`,
  boxShadow: `0 0 0 1px ${alpha($color, 0.12)}`,
  transform: 'translate(-50%, -50%) scale(0.15)',
  opacity: 0,
  animation: `${ripple} ${$duration}ms ease-in-out infinite`,
  animationDelay: `${$delay}ms`,
  willChange: 'transform, opacity',
}));

type RippleWavesProps = {
  size?: number;
  height?: number;
  width?: number;
  color?: string;

  interval?: number;
  count?: number;
  pauseAfterBurst?: number;
};

export function RippleWaves(props: RippleWavesProps) {
  const { palette } = useTheme();
  const {
    size = 1080,
    width = 0,
    height = 0,
    color = palette.accent,

    interval = 500,
    count = 3,
    pauseAfterBurst = 3000,
  } = props;

  const cycleDuration = (count - 1) * interval + pauseAfterBurst;

  const delays = Array.from({ length: count }, (_, i) => i * interval);

  return (
    <WavesRoot>
      {delays.map((d, idx) => (
        <Wave
          key={idx}
          $size={size}
          $width={width}
          $height={height}
          $color={color}
          $duration={cycleDuration}
          $delay={d}
        />
      ))}
    </WavesRoot>
  );
}
